// src/components/ui/Toast.tsx
'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ButtonV2 } from './ButtonV2';

// V2 Design: cva-based variants with glass effect
const toastVariants = cva(
  'rounded-lg p-4 shadow-lg flex items-start gap-3 backdrop-blur-md border transition-all duration-200',
  {
    variants: {
      type: {
        success: 'bg-success/15 text-success border-success/30 shadow-success/10',
        error: 'bg-destructive/15 text-destructive border-destructive/30 shadow-destructive/10',
        warning: 'bg-warning/15 text-warning border-warning/30 shadow-warning/10',
        info: 'bg-info/15 text-info border-info/30 shadow-info/10',
      },
    },
    defaultVariants: {
      type: 'info',
    },
  }
);

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: ToastItem[];
  addToast: (toast: Omit<ToastItem, 'id'>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

function genId() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<ToastItem, 'id'>) => {
    const id = genId();
    const duration = toast.duration ?? 5000;
    setToasts((prev) => [...prev, { ...toast, id }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-notification space-y-3 w-full max-w-sm pointer-events-none">
          <AnimatePresence mode="popLayout">
            {toasts.map((t) => (
              <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
            ))}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');

  // Wrapper providing shadcn-style toast({ title, description, variant }) API
  const toast = useCallback(
    (opts: { title: string; description?: string; variant?: 'default' | 'destructive' }) => {
      ctx.addToast({
        type: opts.variant === 'destructive' ? 'error' : 'success',
        title: opts.title,
        description: opts.description,
      });
    },
    [ctx]
  );

  return { ...ctx, toast };
}

function iconFor(type: ToastType) {
  const cls = 'h-5 w-5';
  switch (type) {
    case 'success':
      return <CheckCircleIcon className={cls} />;
    case 'error':
      return <ExclamationCircleIcon className={cls} />;
    case 'warning':
      return <ExclamationCircleIcon className={cls} />;
    case 'info':
    default:
      return <InformationCircleIcon className={cls} />;
  }
}

const Toast = React.forwardRef<HTMLDivElement, { toast: ToastItem; onClose: () => void }>(
  function Toast({ toast, onClose }, ref) {
    useEffect(() => {
      function onKey(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
      }
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
      <motion.div
        ref={ref}
        layout
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, x: 100, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className={cn(toastVariants({ type: toast.type }), 'pointer-events-auto')}
        role="status"
        aria-live="polite"
      >
        <div className="mt-0.5 shrink-0" aria-hidden="true">{iconFor(toast.type)}</div>
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{toast.title}</p>
          {toast.description && <p className="text-sm opacity-80 break-words">{toast.description}</p>}
        </div>
        <ButtonV2 variant="ghost" size="icon" aria-label="Dismiss" onClick={onClose} className="shrink-0 -mr-1 -mt-1">
          <XMarkIcon className="h-4 w-4" />
        </ButtonV2>
      </motion.div>
    );
  }
);

